import { Controller } from "@hotwired/stimulus"

// Scrollspy for the settings section-nav rail. Highlights the rail link for the
// section nearest the top of the scroll region as the user scrolls. Clicking a
// link smooth-scrolls within the panel (the sections live in a nested scroll
// container) and pins that link active until the user scrolls again — without
// the pin, the trailing sections, which can't reach the top of a tall viewport,
// could never stay highlighted. Intercepting the click also keeps Turbo from
// treating the hash link as a full-page navigation.
export default class extends Controller {
  static targets = ["link"]

  connect() {
    this.sections = this.linkTargets
      .map((link) => document.getElementById(link.dataset.section))
      .filter(Boolean)
    this.root = this.element.closest(".overflow-y-auto")
    if (!this.root) return

    this.pinned = false
    this.ticking = false
    this.onScroll = this.onScroll.bind(this)
    this.unpin = () => {
      this.pinned = false
    }

    this.root.addEventListener("scroll", this.onScroll, { passive: true })
    // A real user-initiated scroll releases the click pin (programmatic
    // smooth-scrolling fires neither of these).
    this.root.addEventListener("wheel", this.unpin, { passive: true })
    this.root.addEventListener("touchstart", this.unpin, { passive: true })
    this.root.addEventListener("keydown", this.unpin)

    this.update()
  }

  disconnect() {
    if (!this.root) return
    this.root.removeEventListener("scroll", this.onScroll)
    this.root.removeEventListener("wheel", this.unpin)
    this.root.removeEventListener("touchstart", this.unpin)
    this.root.removeEventListener("keydown", this.unpin)
  }

  onScroll() {
    if (this.ticking) return
    this.ticking = true
    requestAnimationFrame(() => {
      this.ticking = false
      this.update()
    })
  }

  activate(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.section
    this.pinned = true
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" })
    this.setActive(id)
  }

  update() {
    if (this.pinned || !this.sections.length) return
    // Active = the last section whose top has scrolled to (just past) the top.
    const line = this.root.getBoundingClientRect().top + 40
    let current = this.sections[0]
    for (const section of this.sections) {
      if (section.getBoundingClientRect().top <= line) current = section
      else break
    }
    this.setActive(current.id)
  }

  setActive(section) {
    this.linkTargets.forEach((link) => {
      const active = link.dataset.section === section
      link.classList.toggle("bg-vivid-purple/12", active)
      link.classList.toggle("text-vivid-purple", active)
      link.classList.toggle("dark:text-white", active)
      link.classList.toggle("font-medium", active)
      link.classList.toggle("text-app-text-secondary", !active)
      link.classList.toggle("hover:bg-app-surface-hover", !active)
      link.classList.toggle("hover:text-app-text", !active)
    })
  }
}
